import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { Button, Pagination } from '@dsi/react-eds';
import { useTheme } from '../../hooks/useTheme';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { AxiosError } from 'axios';
import { niiService } from '../../services/niiService';
import { GplScriptBundle } from './types/nii.types';
import styles from './NiiLandingPage.module.css';

const ScriptManagement: React.FC = () => {
  const { theme } = useTheme();
  const { addNotifications } = useSwobNotifications();

  // Upload form state
  const [gplVersion, setGplVersion] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Packages table state
  const [selectedBundle, setSelectedBundle] = useState<GplScriptBundle | null>(null);
  const [tablePagination, setTablePagination] = useState({ pageIndex: 0, pageSize: 6 });

  // Script detail pagination
  const [scriptPagination, setScriptPagination] = useState({ pageIndex: 0, pageSize: 6 });

  // Fetch GPL script bundles
  const { data: bundles = [], isLoading, refetch: refetchBundles } = useQuery({
    queryKey: ['gpl-script-bundles'],
    queryFn: () => niiService.listGplScripts(),
    refetchOnWindowFocus: false
  });

  // Paginated bundles for the table
  const bundlePageCount = Math.ceil(bundles.length / tablePagination.pageSize);
  const paginatedBundles = bundles.slice(
    tablePagination.pageIndex * tablePagination.pageSize,
    (tablePagination.pageIndex + 1) * tablePagination.pageSize
  );

  // Paginated scripts for the detail section
  const scripts = selectedBundle?.contents || [];
  const scriptPageCount = Math.ceil(scripts.length / scriptPagination.pageSize);
  const paginatedScripts = scripts.slice(
    scriptPagination.pageIndex * scriptPagination.pageSize,
    (scriptPagination.pageIndex + 1) * scriptPagination.pageSize
  );

  // Upload handler
  const handleUpload = async () => {
    if (!gplVersion.trim() || !selectedFile) return;
    setUploading(true);
    try {
      const result = await niiService.uploadGplScripts(selectedFile, gplVersion);
      addNotifications([
        {
          title: 'Upload Success',
          icon: 'icon-check',
          description: `GPL scripts uploaded: ${result.contents?.length || 0} scripts detected in ${result.filename}`,
          timestamp: new Date(),
          color: 'green'
        }
      ]);
      setGplVersion('');
      setSelectedFile(null);
      refetchBundles();
    } catch (error) {
      addNotifications([
        {
          title: 'Upload Failed',
          icon: 'icon-triangle-warning',
          description: error instanceof AxiosError ? error.response?.data?.detail || error.message : 'Failed to upload GPL scripts.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setUploading(false);
    }
  };

  // Open script in new tab
  const handleOpenScript = async (scriptName: string) => {
    if (!selectedBundle) return;
    try {
      const content = await niiService.getScriptContent(selectedBundle.uploadId, scriptName);
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description: error instanceof AxiosError ? error.response?.data?.message || error.message : 'Failed to load script content.',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    }
  };

  return (
    <div className={theme ? 'light-theme' : 'dark-theme'}>
      <div
        className={`${styles.settingsContainer} ${styles.settingsContainerPadding} ${theme ? styles.settingsContainerLight : styles.settingsContainerDark}`}
      >
        <div className={styles.settingsTitleContainer}>
          {/* Page header */}
          <div className={styles.settingsTitle}>GPL Audit Script Management</div>
          <p className={styles.subtitle}>Upload and manage GPL audit scripts for network compliance checks.</p>

          {/* Two-panel layout */}
          <div style={{ display: 'flex', gap: '24px', marginTop: '16px' }}>
            {/* Left panel — Upload form */}
            <div style={{ flex: '0 0 35%', minWidth: 0 }}>
              <h3 style={{ marginBottom: '16px' }}>Upload GPL Scripts</h3>

              {/* GPL Package Name */}
              <label style={{ fontWeight: 500 }}>
                GPL Package Name <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                type="text"
                className="input"
                placeholder="Type"
                value={gplVersion}
                onChange={(e) => setGplVersion(e.target.value)}
                style={{ width: '100%', marginTop: '4px', marginBottom: '16px' }}
              />

              {/* File drop zone */}
              <label style={{ fontWeight: 500 }}>
                GPL Package Files{' '}
                <i className="icon icon-info" style={{ fontSize: '12px', opacity: 0.6 }} />
              </label>
              <div
                className="drag-and-drop"
                style={{ marginTop: '8px' }}
              >
                <form
                  className="drag-and-drop-area"
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    const file = e.dataTransfer.files[0];
                    if (file && file.name.endsWith('.zip')) {
                      setSelectedFile(file);
                    }
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  style={{ cursor: 'pointer', backgroundColor: dragOver ? 'rgba(0,105,194,0.05)' : undefined }}
                >
                  <i className="icon icon-upload"></i>
                  <p>
                    Drag & drop your file here or<br />
                    <label role="button" style={{ color: '#0069c2', textDecoration: 'underline', cursor: 'pointer' }}>
                      select to browse
                    </label>
                  </p>
                  <p>
                    Supported format: .zip<br />
                    Max: 1 file
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".zip"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setSelectedFile(file);
                      e.target.value = '';
                    }}
                  />
                </form>
              </div>

              {/* Selected file display */}
              {selectedFile && (
                <div style={{ marginTop: '12px' }}>
                  <span style={{ fontSize: '12px', color: '#878787' }}>Uploaded files:</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', padding: '6px 10px', border: '1px solid #878787', borderRadius: '4px', width: 'fit-content' }}>
                    <i className="icon icon-check" style={{ color: '#28a745' }} />
                    <span>{selectedFile.name}</span>
                    <i
                      className="icon icon-cross"
                      style={{ cursor: 'pointer', opacity: 0.6 }}
                      onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                    />
                  </div>
                </div>
              )}

              {/* Upload button */}
              <div style={{ marginTop: '16px' }}>
                <ReactEDSWrapper
                  component={Button}
                  primary
                  onClick={handleUpload}
                  disabled={!gplVersion.trim() || !selectedFile || uploading}
                >
                  <i className="icon icon-plus" style={{ marginRight: '4px' }} />
                  {uploading ? 'Uploading…' : 'Upload'}
                </ReactEDSWrapper>
              </div>
            </div>

            {/* Right panel — Packages table */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h3>
                  GPL Script Releases{' '}
                  <span style={{ fontSize: '12px', color: '#878787', fontWeight: 400 }}>
                    {bundles.length > 0
                      ? `${tablePagination.pageIndex * tablePagination.pageSize + 1} - ${Math.min((tablePagination.pageIndex + 1) * tablePagination.pageSize, bundles.length)} of ${bundles.length} items`
                      : ''}
                  </span>
                </h3>
                <ReactEDSWrapper component={Button} onClick={() => refetchBundles()}>
                  <i className="icon icon-reload" style={{ marginRight: '4px' }} />
                  Refresh
                </ReactEDSWrapper>
              </div>

              {isLoading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '24px 0' }}>
                  <div className="loader small" />
                  <p>Loading scripts…</p>
                </div>
              )}

              {!isLoading && bundles.length === 0 && (
                <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                  <i className="icon icon-info" style={{ fontSize: '24px', display: 'block', marginBottom: '8px' }} />
                  <div style={{ fontWeight: 500 }}>No GPL Scripts uploaded yet</div>
                  <p style={{ color: '#878787', marginTop: '4px' }}>Please, upload a script archive file</p>
                </div>
              )}

              {!isLoading && bundles.length > 0 && (
                <>
                  <div className="table">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '12px 16px', cursor: 'pointer' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              GPL Package Name <i className="icon icon-sorting" />
                            </div>
                          </th>
                          <th style={{ textAlign: 'left', padding: '12px 16px', cursor: 'pointer' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              Script <i className="icon icon-sorting" />
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedBundles.map((bundle) => (
                          <tr
                            key={bundle.uploadId}
                            style={{
                              cursor: 'pointer',
                              transition: 'background-color 0.2s ease',
                              backgroundColor: selectedBundle?.uploadId === bundle.uploadId
                                ? (theme ? '#d4e8fc' : '#1a3a5c')
                                : ''
                            }}
                            onMouseEnter={(e) => {
                              if (selectedBundle?.uploadId !== bundle.uploadId) {
                                e.currentTarget.style.backgroundColor = !theme ? '#404040' : '#DCDCDC';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (selectedBundle?.uploadId !== bundle.uploadId) {
                                e.currentTarget.style.backgroundColor = '';
                              }
                            }}
                            onClick={() => {
                              setSelectedBundle(selectedBundle?.uploadId === bundle.uploadId ? null : bundle);
                              setScriptPagination({ pageIndex: 0, pageSize: 6 });
                            }}
                          >
                            <td style={{ padding: '12px 16px', borderTop: `1px solid ${!theme ? '#404040' : '#242424'}` }}>
                              {bundle.gplVersion}
                            </td>
                            <td style={{ padding: '12px 16px', borderTop: `1px solid ${!theme ? '#404040' : '#242424'}` }}>
                              {bundle.contents?.length || 0}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {bundlePageCount > 0 && (
                    <ReactEDSWrapper
                      component={Pagination}
                      numPages={bundlePageCount}
                      selectedPage={tablePagination.pageIndex + 1}
                      onPaginationChangePage={(page: number) => {
                        setTablePagination((prev) => ({ ...prev, pageIndex: page - 1 }));
                      }}
                      optionsList={[
                        { value: '6', label: '6', active: tablePagination.pageSize === 6 },
                        { value: '10', label: '10', active: tablePagination.pageSize === 10 },
                        { value: '20', label: '20', active: tablePagination.pageSize === 20 }
                      ]}
                      onPaginationChangeSelect={(size: string) => {
                        setTablePagination({ pageSize: Number(size), pageIndex: 0 });
                      }}
                      withActions
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {/* Bottom section — Script details */}
          {selectedBundle && (
            <div style={{ marginTop: '24px' }}>
              <h3 style={{ marginBottom: '8px' }}>
                {selectedBundle.gplVersion} - Scripts{' '}
                <span style={{ fontSize: '12px', color: '#878787', fontWeight: 400 }}>
                  {selectedBundle.contents?.length > 0
                    ? `${scriptPagination.pageIndex * scriptPagination.pageSize + 1} - ${Math.min((scriptPagination.pageIndex + 1) * scriptPagination.pageSize, selectedBundle.contents.length)} of ${selectedBundle.contents.length} items`
                    : ''}
                </span>
              </h3>

              <div className="table">
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '12px 16px', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Filename <i className="icon icon-sorting" />
                        </div>
                      </th>
                      <th style={{ textAlign: 'left', padding: '12px 16px', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Size <i className="icon icon-sorting" />
                        </div>
                      </th>
                      <th style={{ textAlign: 'left', padding: '12px 16px', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          Modified <i className="icon icon-sorting" />
                        </div>
                      </th>
                      <th style={{ textAlign: 'right', padding: '12px 16px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedScripts.map((scriptName) => (
                      <tr
                        key={scriptName}
                        style={{ transition: 'background-color 0.2s ease' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = !theme ? '#404040' : '#DCDCDC';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = '';
                        }}
                      >
                        <td style={{ padding: '12px 16px', borderTop: `1px solid ${!theme ? '#404040' : '#242424'}` }}>
                          {scriptName}
                        </td>
                        <td style={{ padding: '12px 16px', borderTop: `1px solid ${!theme ? '#404040' : '#242424'}` }}>
                          {selectedBundle.contents.length > 0
                            ? `${(selectedBundle.sizeBytes / selectedBundle.contents.length / 1024).toFixed(1)} KB`
                            : '—'}
                        </td>
                        <td style={{ padding: '12px 16px', borderTop: `1px solid ${!theme ? '#404040' : '#242424'}` }}>
                          {new Date(selectedBundle.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '12px 16px', borderTop: `1px solid ${!theme ? '#404040' : '#242424'}`, textAlign: 'right' }}>
                          <ReactEDSWrapper
                            component={Button}
                            onClick={() => handleOpenScript(scriptName)}
                          >
                            Open <i className="icon icon-chevron-right" style={{ marginLeft: '4px' }} />
                          </ReactEDSWrapper>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {scriptPageCount > 0 && (
                <ReactEDSWrapper
                  component={Pagination}
                  numPages={scriptPageCount}
                  selectedPage={scriptPagination.pageIndex + 1}
                  onPaginationChangePage={(page: number) => {
                    setScriptPagination((prev) => ({ ...prev, pageIndex: page - 1 }));
                  }}
                  optionsList={[
                    { value: '6', label: '6', active: scriptPagination.pageSize === 6 },
                    { value: '10', label: '10', active: scriptPagination.pageSize === 10 },
                    { value: '20', label: '20', active: scriptPagination.pageSize === 20 }
                  ]}
                  onPaginationChangeSelect={(size: string) => {
                    setScriptPagination({ pageSize: Number(size), pageIndex: 0 });
                  }}
                  withActions
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScriptManagement;
